import { computedAsync } from '@vueuse/core';
import { resolve } from './asyncIntegration';
import type { PossiblyAsyncIntegration } from './types/asyncIntegration';
import { type IntegrationsStore } from './types/integrationsStore';
import { type InstallOptions, install } from './utils/install';
import { type PatchOptions, patch } from './utils/patch';
import { type UninstallOptions, uninstall } from './utils/uninstall';

export const useIntegrations = <Integrations extends Record<string, PossiblyAsyncIntegration>>(
  store: IntegrationsStore<Integrations>,
) => {
  return {
    integrations: computedAsync(() => Object.values(store.integrations), []),
    install: async <C extends keyof Integrations>(
      code: C,
      config: <Config>(config: Config) => Config = (c) => c,
      options?: InstallOptions,
    ) => await install(store, await resolve(store.integrations[code], store), config, options),
    preSetup: async <C extends keyof Integrations, T>(code: C, payload: T) =>
      (await resolve(store.integrations[code], store)).preSetup(payload),
    uninstall: async <C extends keyof Integrations>(
      code: C,
      config: <Config>(config: Config) => Config = (c) => c,
      options?: UninstallOptions,
    ) => await uninstall(store, await resolve(store.integrations[code], store), config, options),
    patch: async <C extends keyof Integrations, Config>(
      code: C,
      config: Config,
      options?: PatchOptions,
    ) => patch(store, await resolve(store.integrations[code], store), config, options),
  };
};
